import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaGamepad, FaBrain, FaGraduationCap, FaUserTie, FaUser } from "react-icons/fa";
import { useInView } from "../hooks/useInView";

// Create a separate component for service card
const ServiceCard = ({ service, index }) => {
  const [ref, isVisible] = useInView();
  
  return (
    <div 
      ref={ref}
      className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
      style={{
        opacity: 0,
        transform: 'translateY(20px)',
        animation: isVisible ? `fadeInUp 0.5s ease-in-out forwards ${index * 0.2}s` : 'none'
      }}
    >
      <div className="p-6">
        <div 
          className="text-secondary flex justify-center"
          style={{
            opacity: 0,
            transform: 'scale(0.9)',
            animation: isVisible ? `scaleIn 0.3s ease-in-out forwards ${index * 0.2 + 0.3}s` : 'none'
          }}
        >
          {service.icon}
        </div>
        <h2 className="text-2xl font-semibold text-secondary mb-3 text-center">{service.title}</h2>
        <p className="text-gray-600 mb-6 text-center">{service.description}</p>
        <Link
          to={`/${service.slug}`}
          className="block text-center bg-secondary text-label py-2 px-4 rounded-md hover:opacity-90 transition-all duration-300"
        >
          Daha Fazla Bilgi
        </Link>
      </div>
    </div>
  );
};

// Create a separate component for the footer
const Footer = () => {
  const [ref, isVisible] = useInView();
  
  return (
    <div 
      ref={ref}
      className="text-center mt-10"
      style={{
        opacity: 0,
        transform: 'translateY(20px)',
        animation: isVisible ? 'fadeInUp 0.5s ease-in-out forwards' : 'none'
      }}
    >
       <p className="text-gray-700">
          Daha fazla bilgi için <Link to="/iletisim" className="text-secondary font-semibold hover:underline">İletişim</Link> sayfasını ziyaret edebilirsiniz. 
        </p>
    </div>
  );
};

function ServicesPage() {
  const services = [
    {
      id: 1,
      title: "Bireysel Yetişkin Psikoterapisi",
      description: "Yetişkinler için bireysel psikoterapi seansları. Profesyonel ve gizlilik esaslı terapi süreci.",
      icon: <FaUser className="text-4xl mb-4" />,
      slug: "bireysel-yetiskin-psikoterapisi"
    },
    {
      id: 2,
      title: "MOXO Testi",
      description: "Dikkat eksikliği ve hiperaktivite değerlendirmesi için bilimsel ve kapsamlı analiz.",
      icon: <FaBrain className="text-4xl mb-4" />,
      slug: "moxo-testi"
    },
    {
      id: 3,
      title: "Öğrenci Danışmanlığı",
      description: "Akademik başarı ve motivasyon için profesyonel destek ve rehberlik hizmeti.",
      icon: <FaGraduationCap className="text-4xl mb-4" />,
      slug: "ogrenci-danismanligi"
    },
    {
      id: 4,
      title: "Oyun Terapisi",
      description: "Çocuklar için psikolojik destek ve duygusal gelişim hizmeti.",
      icon: <FaGamepad className="text-4xl mb-4" />,
      slug: "oyun-terapisi"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Çalışma Alanlarım | Bireysel Yetişkin Psikoterapisi, Ergen Psikoterapisi, Oyun Terapisi</title>
        <meta 
          name="description" 
          content="Psikolog Yağmur Demiröz - Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi, MOXO Testi, öğrenci danışmanlığı hizmetleri. İstanbul, Etiler, Ümraniye." 
        />
        <meta name="keywords" content="bireysel yetişkin psikoterapisi, ergen psikoterapisi, yetişkin terapisi, oyun terapisi, MOXO testi, öğrenci danışmanlığı, psikolojik destek, İstanbul psikolog" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Psikolojik Danışmanlık Hizmetleri",
            "provider": {
              "@type": "Psychologist",
              "name": "Yağmur Demiröz",
              "jobTitle": "Psikolog"
            },
            "serviceType": [
              "Bireysel Yetişkin Psikoterapisi",
              "Ergen Psikoterapisi",
              "Yetişkin Terapisi",
              "MOXO Testi",
              "Öğrenci Danışmanlığı",
              "Oyun Terapisi"
            ],
            "description": "Psikolog Yağmur Demiröz tarafından sunulan psikoterapi hizmetleri. Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi, dikkat testi, akademik danışmanlık hizmetleri içerir.",
            "url": "https://yagmurdemiroz.com/calisma-alanlarim",
            "areaServed": {
              "@type": "City",
              "name": "İstanbul"
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <div className="animate-fadeIn">
            <h1 className="text-4xl font-bold text-secondary">Çalışma Alanlarım</h1>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>

        <Footer />
      </div>
    </div>
  );
}

export default ServicesPage;